import { useSiteBranding } from "@/api/branding";
import cfWordmark from "@/assets/logo-green.svg";
import cfSquareMark from "@/assets/logo-green-sq.svg";
import { cn } from "@/lib/utils";
import { orgBrandOverride } from "@/lib/branding";
import { useAppContext } from "@/providers/app-context";

type Variant = "wordmark" | "square";

/**
 * Per-variant slot dims + bundled CF fallback. `object-contain` keeps an
 * org-supplied logo of any aspect ratio from exceeding the slot.
 */
const VARIANTS: Record<Variant, { className: string; fallback: string }> = {
  wordmark: {
    className: "h-8 max-w-48 object-contain object-left",
    fallback: cfWordmark,
  },
  square: {
    className: "h-full w-full object-contain",
    fallback: cfSquareMark,
  },
};

type Props = {
  variant: Variant;
  className?: string;
  alt?: string;
};

/**
 * Precedence: org override (whitelabel + toggle + uploaded logo) → site
 * branding (wordmark variant only — SiteBranding is a single asset, not
 * suited to the square slot) → bundled CF default for the variant. Route
 * remounts of PageBase would otherwise flash CF; the preload effect in
 * `AppContextProvider` warms the browser cache for the org override URL.
 */
export function PlatformBrandLogo({ variant, className, alt = "Logo" }: Props) {
  const branding = useSiteBranding();
  const { actingOrganization } = useAppContext();
  const { className: variantClass, fallback } = VARIANTS[variant];

  const orgOverride = orgBrandOverride(actingOrganization);
  const siteLogo = variant === "wordmark" ? branding.data?.logo_url : null;
  const src = orgOverride ?? siteLogo ?? fallback;

  return <img src={src} alt={alt} className={cn(variantClass, className)} />;
}
